<!--
  Created 10.10.2023
  Price component for the proposal show page.
-->
<template>
  <section class="relative bg-aqua-800 print:break-before-page print:!bg-transparent">
    <!-- Adjustment icon/box -->
    <SectionOptions
      v-if="contentEditable"
      class="z-20 text-white"
      section="prices" />
    <div class="container relative isolate mx-auto px-6 py-12 pb-16 md:py-24 md:pb-28 print:!py-8">
      <!-- Price proposal box -->
      <div
        v-if="contentEditable || proposal.data.sections.prices.visible"
        class="relative flex flex-col rounded bg-aqua-50 p-6 md:p-12">
        <!-- disabled cursor overlay if section is hidden -->
        <div
          class="z-10 hidden"
          :class="{ 'absolute left-0 top-0 !block size-full cursor-not-allowed' : !proposal.data.sections.prices.visible }" />
        <!-- Price proposal content -->
        <div
          class="mb-8 flex flex-col justify-between md:flex-row"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <TextEditableTextarea
            v-if="contentEditable"
            id="section-prices-heading"
            v-model="proposal.data.sections.prices.heading"
            background-style="gray"
            class="lg:mr-2 xl:mr-8 2xl:mr-24"
            :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
              + 'text-2xl font-medium text-gray-800 '
              + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
            :placeholder="$t('proposal.prices_heading_placeholder')"
            :show-buttons="false"
            :saved-changes="savedChanges"
            @edited="store.unsavedChanges.changes.proposal = true;" />
          <h1
            v-else
            class="text-2xl">
            {{ proposal.data?.sections?.prices?.heading ?? $t('proposal.prices_heading_default') }}
          </h1>
          <a class="items-center space-x-2 font-medium hover:cursor-pointer hover:underline max-md:-mb-4 max-md:mt-4 md:flex md:justify-center">
            <TextEditableTextarea
              v-if="contentEditable"
              id="section-prices-cta"
              v-model="proposal.data.sections.prices.cta"
              class="relative"
              background-style="gray"
              tab-rows="1"
              :auto-grow="false"
              text-area-max-length="30"
              editing-indicator-class="!ml-[1px]"
              :text-area-class="'bg-transparent whitespace-pre-wrap md:leading-snug lg:leading-tight '
                + 'inline-block max-md:underline text-right '
                + 'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :placeholder="$t('proposal.prices_cta_placeholder')"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <p
              v-else
              class="inline-block max-md:underline print:hidden"
              @click="showProposalAcceptanceModal = true">{{ proposal.data.sections?.prices?.cta ?? $t('proposal.prices_cta_default') }}</p>
            <ShoppingBagIcon class="inline-block size-5 print:hidden" />
          </a>
        </div>
        <!-- Price summary -->
        <div
          v-if="contentEditable || proposal.data.sections.prices.visible"
          class="grid gap-4 lg:grid-cols-2 lg:gap-8"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <div class="rounded bg-white p-4 md:p-8">
            <TextEditableRichText
              v-if="contentEditable"
              id="priceOffer-column_one"
              v-model="priceOffer.column_one"
              :tab-rows="3"
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <TextEditableRichText
              v-else
              id="priceOffer-column_one"
              v-model="priceOffer.column_one"
              disabled
              :tab-rows="3"
              placeholder=""
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false" />
          </div>
          <div class="rounded bg-white p-4 md:p-8">
            <TextEditableRichText
              v-if="contentEditable"
              id="priceOffer-column_one"
              v-model="priceOffer.column_two"
              :tab-rows="3"
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <TextEditableRichText
              v-else
              id="priceOffer-column_two"
              v-model="priceOffer.column_two"
              disabled
              :tab-rows="3"
              placeholder=""
              background-style="white"
              class="mt-2"
              :text-area-class="'bg-transparent !text-base !leading-snug text-gray-900 whitespace-pre-wrap ' +
                'placeholder:italic placeholder:text-gray-500 placeholder:opacity-50 cursor-text'"
              :show-buttons="false" />
          </div>
        </div>
      </div>
      <!-- agent information -->
      <div
        class="mt-4 grid divide-y rounded bg-white max-lg:text-sm lg:mt-8 lg:grid-cols-2 lg:divide-x lg:divide-y-0 print:!divide-y-0 print:bg-gray-50"
        :class="{ 'lg:!grid-cols-1' : !proposal.data.sections.prices.visible && !contentEditable }">
        <div
          v-if="contentEditable || proposal.data.sections.prices.visible"
          class="flex flex-row items-center justify-center gap-8 p-6 lg:gap-12 lg:p-12"
          :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
          <Avatar
            :src="proposal.project.manager?.profile_photo_url ?? proposal.creator.profile_photo_url"
            :alt="proposal.project.manager?.name ?? proposal.creator.name"
            size="6xl" />
          <div class="space-y-1">
            {{ $t('proposal.greeting_kind_regards') }}<br>
            <strong>{{ proposal.project.manager?.name ?? proposal.creator.name }}</strong><br>
            <span class="mt-4 inline-block">
              <a
                v-if="proposal.project.manager?.phone || proposal.creator.phone"
                :href="'tel:' + (proposal.project.manager?.phone ?? proposal.creator.phone).replace(' ', '')"
                class="block cursor-pointer underline print:no-underline">{{ proposal.project.manager?.phone ?? proposal.creator.phone }}</a>
              <a
                v-if="proposal.project.manager?.email || proposal.creator.email"
                :href="'mailto:' + proposal.project.manager?.email ?? proposal.creator.email"
                class="block cursor-pointer underline print:no-underline">{{ proposal.project.manager?.email ?? proposal.creator.email }}</a>
            </span>
          </div>
        </div>
        <div class="flex flex-col items-center justify-center space-y-1 p-6 lg:p-12">
          {{ $t('proposal.delivered_by_label') }}<br>
          <strong>{{ proposal.project.team.name }}</strong> <span v-if="proposal.project.team.org_no">({{ $t('proposal.org_no_label') }}: {{ proposal.project.team.org_no }})</span><br>
          <span
            v-if="contentEditable || proposal.data.sections.prices.visible"
            :class="{ 'pointer-events-none opacity-50' : !proposal.data.sections.prices.visible }">
            <a
              :href="proposal.terms?.original_url ?? ''"
              target="_blank"
              class="block cursor-pointer text-center underline print:hidden">{{ $t('proposal.see_terms_link') }}</a>
            <a class="block cursor-pointer text-center underline print:hidden">{{ $t('proposal.privacy_and_cookies_link') }}</a>
          </span>
        </div>
      </div>
      <div
        class="mt-6 flex flex-col justify-around max-md:space-y-4 md:mt-12 md:flex-row"
        :class="[
          { 'cursor-not-allowed opacity-50' : contentEditable && !proposal.data.sections.prices.visible }
        ]">
        <!-- Hide PDF button for now -->
        <!-- <button
          v-if="!contentEditable"
          class="rounded border-2 border-transparent bg-white px-4 py-2 text-center font-medium text-gray-700 shadow-lg
            hover:border-gray-700 hover:text-gray-900 print:hidden"
          @click="downloadPDF">
          <div class="flex items-center justify-center space-x-2">
            <span class="inline-block">Tilbudet som PDF</span>
            <DocumentTextIcon class="inline-block size-5" />
          </div>
        </button> -->
        <button
          class="rounded border-2 border-transparent bg-white px-4 py-2 text-center font-medium text-gray-700 shadow-lg
            hover:border-gray-700 hover:text-gray-900 print:hidden"
          :class="[
            { 'hidden': !contentEditable && !proposal.data.sections.prices.visible },
            { 'pointer-events-none' : !proposal.data.sections.prices.visible }
          ]"
          @click="showProposalAcceptanceModal = true">
          <div class="flex items-center justify-center space-x-2">
            <span class="inline-block">{{ proposal.data.sections?.prices?.cta ?? $t('proposal.prices_cta_default') }}</span>
            <ShoppingBagIcon class="inline-block size-5" />
          </div>
        </button>
      </div>
    </div>
    <!-- Offer acceptance modal -->
    <SimpleModal
      z-index-class="z-[60]"
      :box-class="[
        'w-11/12 md:w-4/5 lg:w-10/12 max-w-[900px] max-h-[80vh] relative z-50',
        contentEditable ? 'overflow-hidden' : 'overflow-y-auto'
      ]"
      :show="showProposalAcceptanceModal"
      @update:show="(value) => showProposalAcceptanceModal = value">
      <template #content>
        <!-- Block acceptance module for editing, and give notice of missing terms -->
        <div
          v-if="contentEditable"
          class="absolute z-10 flex size-full flex-col items-center justify-center space-y-8 rounded-lg bg-gray-300/90 p-12">
          <div
            v-if="!proposal.terms"
            class="w-8/12">
            <h2 class="font-bold text-red-700">
              {{ $t('proposal.modal_warning_title') }}
            </h2>
            <p class="font-medium text-red-700">
              {{ $t('proposal.modal_warning_terms_unavailable_line1') }}
              <br>
              {{ $t('proposal.modal_warning_terms_unavailable_line2') }}
            </p>
          </div>
          <!-- If in create proposal we display notice accepting only works in proposal versions -->
          <div class="w-8/12">
            <h2 class="font-bold text-yellow-800">
              {{ $t('proposal.modal_note_title') }}
            </h2>
            <p class="font-medium text-yellow-800">
              {{ $t('proposal.modal_note_line1') }}<br>
              {{ $t('proposal.modal_note_line2') }}
            </p>
          </div>
          <div class="w-8/12">
            <button
              class="rounded border-2 border-transparent bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-lg hover:border-gray-700 hover:text-gray-900"
              @click="showProposalAcceptanceModal = false">
              {{ $t('proposal.close_button') }}
            </button>
          </div>
        </div>
        <!-- If proposal version accepted -->
        <div
          v-if="(store.proposal.accepted | !!store.proposal.accepted_sibling_id) && !contentEditable"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            {{ $t('proposal.accepted_title') }}
          </h2>
          <p
            v-if="!!store.proposal.accepted_sibling_id"
            class="my-4 mb-8 text-sm">
            {{ $t('proposal.accepted_other_version_details', {
              version: proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionNumber ?? 0,
              versionId: proposalVersions?.find(v => v.proposalVersionId === store.proposal.accepted_sibling_id)?.proposalVersionId ?? 0,
            }) }}
            <br>
            <br>
            {{ $t('proposal.accepted_current_viewing', {
              version: proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionNumber ?? 0,
              versionId: proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionId ?? 0,
            }) }}
          </p>
          <p
            v-else
            class="my-4 mb-8 text-sm">
            {{ $t('proposal.accepted_details', {
              version: proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionNumber ?? 0,
              versionId: proposalVersions?.find(v => v.proposalVersionId === store.proposal.proposalVersionId)?.proposalVersionId ?? 0,
            }) }}
          </p>
        </div>
        <!-- If proposal version waiting for verification -->
        <div
          v-else-if="!retry && proposal.verification_email_sent && !contentEditable"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            {{ $t('proposal.accepted_title') }}
          </h2>
          <p>{{ $t('proposal.waiting_email_verification') }}</p>

          <p class="mt-5 text-sm">
            {{ $t('proposal.didnt_receive_email') }} <a
              href="#"
              class="text-indigo-500 hover:text-indigo-800"
              @click="retry = true">{{ $t('proposal.try_again_link') }}</a>.
          </p>
        </div>
        <!-- If form was successfully sent -->
        <div
          v-else-if="!retry && formSent"
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            {{ $t('proposal.thank_you_title') }}
          </h2>
          <p>{{ $t('proposal.email_sent_verification') }}</p>

          <p class="mt-5 text-sm">
            {{ $t('proposal.didnt_receive_email') }} <a
              href="#"
              class="text-indigo-500 hover:text-indigo-800"
              @click="retry = true">{{ $t('proposal.try_again_link') }}</a>.
          </p>
        </div>
        <!-- If not accepted -->
        <div
          v-else
          class="max-w-3xl p-8">
          <h2 class="text-lg font-medium">
            {{ $t('proposal.accept_proposal_title') }}
          </h2>
          <p class="mb-8 text-sm">
            {{ $t('proposal.accept_proposal_intro_line1') }}
            <br>
            {{ $t('proposal.accept_proposal_intro_line2') }}
          </p>
          <form
            @submit.prevent="form.post(route('proposal.accept', { hash: proposal.hash }), {
              preserveScroll: true,
              preserveState: true,
              onSuccess: () => hideForm()
            })">
            <div class="grid grid-cols-3 items-center gap-4">
              <label class="block text-xs font-medium text-gray-700"> {{ $t('proposal.you_label') }} </label>
              <div class="col-span-2">
                <p> {{ proposal.client.name }} </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> {{ $t('proposal.proposal_label') }} </label>
              <div class="col-span-2">
                <p v-if="contentEditable">
                  {{ proposal.title }}
                </p>
                <p v-else>
                  {{ proposal.title }} (v{{ proposalVersions?.find(v => v.id === proposal.proposalVersionId)?.proposalVersionNumber ?? 0 }})
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> {{ $t('proposal.travel_date_label') }} </label>
              <div class="col-span-2">
                {{ travelDate }}
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> {{ $t('proposal.number_of_travelers_label') }} </label>
              <div class="col-span-2">
                <p> {{ proposal.pax }} </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-xs font-medium text-gray-700"> {{ $t('proposal.amount_per_pax_label') }} </label>
              <div class="col-span-2">
                <p> {{ proposal.amount_pr_pax }} </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> {{ $t('proposal.your_full_name_label') }} </label>
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  id="name"
                  v-model="form.name"
                  name="name"
                  type="text"
                  required="required"
                  class="block min-w-[300px] max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder=""
                  @change="form.clearErrors('name')">
                <p
                  v-if="form.errors.name"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.name }}
                </p>
              </div>

              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> {{ $t('proposal.your_email_label') }} </label>
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <input
                  id="email"
                  v-model="form.email"
                  name="email"
                  type="email"
                  required="required"
                  class="block min-w-[300px] max-w-lg rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder=""
                  @change="form.clearErrors('email')">
                <p
                  v-if="form.errors.email"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.email }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-3 items-center gap-4 pt-4">
              <label class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"> {{ $t('proposal.terms_and_conditions_label') }} </label>
              <div class="col-span-2 flex flex-row items-center space-x-2">
                <input
                  id="accept-terms"
                  v-model="form.accept_terms"
                  :disabled="!proposal.terms"
                  :title="!proposal.terms ? $t('proposal.terms_unavailable_title') : $t('proposal.accept_terms_title')"
                  name="accept-terms"
                  type="checkbox"
                  class="block size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  :class="{'cursor-not-allowed opacity-50': !proposal.terms && !contentEditable}">
                <label
                  for="accept-terms"
                  :title="!proposal.terms ? $t('proposal.terms_unavailable_title') : $t('proposal.accept_terms_title')"
                  class="block text-sm font-medium leading-6 text-gray-900"
                  :class="{'cursor-not-allowed opacity-50': !proposal.terms && !contentEditable}">
                  {{ $t('proposal.i_accept_prefix') }} <a
                    :href="proposal.terms?.original_url"
                    target="_blank"
                    class="inline-block cursor-pointer text-center underline">{{ $t('proposal.terms_and_conditions_link_text') }}</a>.
                </label>
                <p
                  v-if="form.errors.accept_terms"
                  class="mt-2 text-sm text-red-600">
                  {{ form.errors.accept_terms }}
                </p>
              </div>
            </div>

            <hr class="my-8">

            <div class="flex justify-end">
              <CreateButton
                class="mr-2"
                type="submit"
                :disabled="form.processing || !form.accept_terms"
                :title="!form.accept_terms ? $t('proposal.cannot_accept_without_terms_title') : $t('proposal.submit_form_title')"
                :class="{'cursor-not-allowed opacity-50': form.processing || !form.accept_terms}">
                {{ $t('proposal.accept_proposal_action') }}
              </CreateButton>
              <CancelButton
                class="ml-2"
                @click="close" />
            </div>
          </form>
        </div>
      </template>
    </SimpleModal>
  </section>
</template>
<script setup>
/* external */
import {
  ShoppingBagIcon,
} from '@heroicons/vue/24/outline';
import Avatar from '@/Components/Avatar.vue';
import { useProposalStore } from '@/Stores/Proposal';
import {
  computed, ref, watch,
} from 'vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import TextEditableRichText from '@/Components/Forms/TextEditableRichText.vue';
import SimpleModal from '@/Components/Modals/SimpleModal.vue';
import { useForm } from '@inertiajs/vue3';
import CreateButton from '@/ComponentsDeprecated/Buttons/CreateButton.vue';
import CancelButton from '@/ComponentsDeprecated/Buttons/CancelButton.vue';
import SectionOptions from '@/Pages/Proposal/Components/SectionOptions.vue';
import moment from 'moment-timezone';
import { isSameMonth } from '@/utils';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
  proposalVersions: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const store = useProposalStore();
const proposal = computed(() => store.proposal);
const priceOffer = computed(() => proposal.value.data.price_offer);
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);

const showProposalAcceptanceModal = ref(false);

const form = useForm({
  name: '',
  email: '',
  accept_terms: false,
});

const formSent = ref(false);
const retry = ref(false);

/**
 * Travel date formatted
 */
const travelDate = computed(() => {
  let dateFormatted = '';
  if (isSameMonth(proposal.value.start_date, proposal.value.end_date)) {
    dateFormatted = moment(proposal.value.start_date).format('D-')
      + moment(proposal.value.end_date).format('LL');
  } else {
    dateFormatted = `${moment(proposal.value.start_date).format('D. MMM')
    } - ${moment(proposal.value.end_date).format('D. MMM YYYY')}`;
  }

  return dateFormatted;
});

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Close the offer acceptance modal
 */
function close() {
  form.reset();
  showProposalAcceptanceModal.value = false;
}

/**
 * Hide form after it is sent
 */
function hideForm() {
  formSent.value = true;
  retry.value = false;
}

/**
 * Trigger PDF download and/or creation if missing
 * TODO: CAN BE DELETED IF NOT CHANGED/UPDATED/IMPLEMENTED BY 01.01.2027
 */
// eslint-disable-next-line no-unused-vars
function downloadPDF() {
  console.log('downloadPDF not implemented');

  // Print button/modal removed, but this is only commented out for later cleanup
  // TODO: Consider removing printMode and its tweaks to all components
  // systemStore.setPrintMode(true);
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

watch(() => form.client, () => {
  form.clearErrors('client_id');
});
watch(() => form.project, () => {
  form.clearErrors('project_id');
});
watch(() => form.range, () => {
  form.clearErrors('range');
});

</script>
